import { type Atom, useSelector } from "@tanstack/react-store";
import { styleWithVars } from "@lib/cssVars";
import { DragHandle6DotIcon } from "@icons";
import {
    getPortalMotionProps,
    type TableMotion,
} from "../../motion/tableMotion";
import type { ColumnIndicatorState } from "./columnDrag";
import type { DragInput, DragPreviewInfo } from "./dragKind";
import type { RowIndicatorState } from "./rowDrag";
import styles from "./dnd.module.scss";

/**
 * The drop line between rows, indented to the depth the row would land at,
 * or a box around the row it would nest into. It slides between targets
 * for pointer drags and jumps for keyboard steps.
 */
export const RowDropIndicator = ({
    indicator,
    input,
}: {
    indicator: Atom<RowIndicatorState | null>;
    input: DragInput;
}) => {
    const state = useSelector(indicator);
    if (!state) {
        return null;
    }
    const isInto = state.target.position === "into";
    return (
        <div
            className={styles.rowIndicator}
            data-kind={isInto ? "into" : "line"}
            data-input={input}
            style={styleWithVars({
                transform: `translateY(${state.top}px)`,
                height: isInto ? `${state.height}px` : undefined,
                "--dg-drop-depth": state.target.depth,
                "--dg-drop-indent": `${state.indent}px`,
            })}
            aria-hidden
        />
    );
};

/** The line between headers where a dragged column would land. */
export const ColumnDropIndicator = ({
    indicator,
}: {
    indicator: Atom<ColumnIndicatorState | null>;
}) => {
    const state = useSelector(indicator);
    if (!state) {
        return null;
    }
    return (
        <div
            className={styles.columnIndicator}
            style={{
                transform: `translate(${state.x}px, ${state.top}px)`,
                height: state.height,
            }}
            aria-hidden
        />
    );
};

/**
 * The lifted chip under the pointer: the row's grip and name, or the
 * column's name. Light by design; the source stays in place, dimmed. It
 * renders in a portal, so it carries the table's motion level and timing.
 */
export const DragPreview = ({
    kind,
    preview,
    motion,
}: {
    kind: "row" | "column";
    preview: DragPreviewInfo;
    motion: TableMotion | null;
}) => (
    <div
        className={styles.previewScope}
        {...(motion ? getPortalMotionProps(motion) : undefined)}
    >
        {kind === "row" ? (
            <div className={styles.rowPreview}>
                <DragHandle6DotIcon aria-hidden />
                <span className={styles.previewLabel}>{preview.label}</span>
            </div>
        ) : (
            <div className={styles.columnPreview}>
                <span className={styles.previewLabel}>{preview.label}</span>
            </div>
        )}
    </div>
);
